import { Authenticated, DataProvider, Refine } from "@refinedev/core";
import {
  ErrorComponent,
  // ThemedLayoutV2,
  // ThemedSiderV2,
  useNotificationProvider,
} from "@refinedev/antd";
import "@refinedev/antd/dist/reset.css";

import routerBindings, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router-v6";
import { App as AntdApp } from "antd";
import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import { authProvider } from "./authProvider";
import { ThemedHeaderV2 } from "./components/layout/header";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { UsersList, UserEdit, UserShow } from "./pages/users";
import { ReportUserList, ReportContentList, ReportContentShow } from "./pages/reports";
import { PlansList, PlanShow, PlanEdit } from "./pages/subscriptions/subscriptionPlans";
import { PayoutsList, PayoutShow } from "./pages/subscriptions/payouts";
import { AdminList, AdminCreate, AdminEdit } from "./pages/admins";
import { WorkoutsList, WorkoutShow, WorkoutEdit, WorkoutCreate } from "./pages/workouts";
import { WorkoutPreferencesList, WorkoutPreferenceEdit, WorkoutPreferenceCreate } from "./pages/workoutPreferences";
import { MissionCreate, MissionEdit, MissionShow, MissionsList } from "./pages/missions";
import ForgotPassword from "./pages/forgotPassword";
import AcceptInvite from "./pages/acceptInvite";
import { Login } from "./pages/login";

import Icon from "../public/favicon.ico";
import { apiUrl } from "./consts";
import { dataProvider } from "./dataProvider";
import {
  UserOutlined, UsergroupAddOutlined, ExclamationCircleOutlined,
  DashboardOutlined, TrophyOutlined, FireOutlined,
  TagOutlined, CheckCircleOutlined, CreditCardOutlined,
  ThunderboltOutlined,
  PictureOutlined,
  FormOutlined,
  CompassOutlined,
  ScheduleOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { ResetPassword } from "./pages/resetPassword.tsx";
import { DashboardPage } from "./pages/dashboard";
import { OfferList } from "./pages/subscriptions/offers/list";
import { OfferCreate } from "./pages/subscriptions/offers/create";
import { OfferEdit } from "./pages/subscriptions/offers/edit";
// import { getStreamDataProvider } from "./getStreamDataProvider";
import { ThemedLayoutV2 } from "./components/layout";
import { ThemedSiderV2 } from "./components/layout/sider";
import { SubscribersList, SubscriptionShow } from "./pages/subscriptions/subscriptions";
import { SettingsPage } from "./pages/settings";
import { SettingOutlined } from "@ant-design/icons";
import { CategoriesList, CategoryCreate, CategoryEdit } from "./pages/categories";
import { BannersList, BannerCreate, BannerEdit } from "./pages/banners";
import { CardTemplatesList, CardTemplateCreate, CardTemplateEdit } from "./pages/cardTemplates";
import { AdCreativesList, AdCreativeCreate, AdCreativeEdit } from "./pages/adCreatives";
import { TournamentsList, TournamentShow } from "./pages/tournaments";
import { ScoutingEventList, ScoutingEventCreate, ScoutingEventShow, ScoutingEventEdit } from "./pages/scoutingEvents";
import { SurveySubmissionsList, SurveySubmissionShow } from "./pages/surveySubmissions";
import { SessionTypesList, SessionTypeEdit } from "./pages/sessionTypes";
import { ScScheduleList, ScScheduleCreate, ScScheduleEdit } from "./pages/scSchedule";
import {
  FitnessScheduleList,
  FitnessScheduleCreate,
  FitnessScheduleEdit,
} from "./pages/fitnessSchedule";
import { LottoLicenceList, LottoLicenceShow } from "./pages/lottoLicences";
import { ClubsList, ClubsCreate, ClubsEdit, ClubsShow } from "./pages/clubs";
import { RevenuePage } from "./pages/revenue";
import { TrainingQuestionnairePage } from "./pages/trainingQuestionnaire";

function App() {
  return (
    <BrowserRouter>
      <ColorModeContextProvider>
        <AntdApp>
          <Refine
            dataProvider={dataProvider(apiUrl)}
            notificationProvider={useNotificationProvider}
            routerProvider={routerBindings}
            authProvider={authProvider}
            resources={[
              {
                name: "dashboard",
                list: "/",
                meta: {
                  label: "Dashboard",
                  icon: <DashboardOutlined />,
                },
              },
              {
                name: "revenue",
                list: "/revenue",
                meta: {
                  label: "Revenue",
                  icon: <DollarOutlined />,
                },
              },
              {
                name: "user",
                list: "/users",
                edit: "/users/edit/:id",
                show: "/users/:id",
                icon: <UserOutlined />,
              },
              {
                name: "workout",
                list: "/workouts",
                create: "/workouts/create",
                edit: "/workouts/edit/:id",
                show: "/workouts/:id",
                icon: <FireOutlined />,
                meta: { hide: true },
              },
              {
                name: "mission",
                list: "/missions",
                create: "/missions/create",
                edit: "/missions/edit/:id",
                show: "/missions/:id",
                icon: <TrophyOutlined />,
                meta: { hide: true },
              },
              {
                name: "report",
                icon: <ExclamationCircleOutlined />,
              },
              {
                name: "report-users",
                parentName: 'report',
                list: "/reports/users",
                // show: "/reports/users/:id",
                icon: <ExclamationCircleOutlined />,

              },
              {
                name: "report-content",
                parentName: 'report',
                list: "/reports/content",
                show: "/reports/content/:id",
                icon: <ExclamationCircleOutlined />,

              },
              {
                name: "admin",
                create: "/admins/create",
                list: "/admins",
                edit: '/admins/edit/:id',
                show: "/admins/:id",
                icon: <UsergroupAddOutlined />,
              },
              {
                name: "lotto-licences",
                list: "/lotto-licences",
                show: "/lotto-licences/:id",
                meta: {
                  label: "Lotto licences",
                  icon: <SafetyCertificateOutlined />,
                },
              },
              {
                name: 'subscription-data',
                icon: <CreditCardOutlined />,
                meta: { hide: true },
              },
              {
                name: "subscription-plan",
                parentName: "subscription-data",
                list: "/subscriptions/subscription-plans",
                edit: "/subscriptions/plans/edit/:id",
                show: "/subscriptions/plans/show/:id",
                icon: <TagOutlined />,
                meta: { hide: true },
              },
              {
                name: "offer",
                parentName: "subscription-data",
                list: "/subscriptions/offers",
                create: "/subscriptions/offers/create",
                edit: "/subscriptions/offers/edit/:id",
                icon: <CheckCircleOutlined />, // Represents daily checkups/tasks
                meta: { hide: true },
              },
              {
                name: "subscription",
                parentName: "subscription-data",
                list: "/subscriptions",
                show: "/subscriptions/show/:id",
                icon: <UserOutlined />,
                meta: { hide: true },
              },
              {
                name: "payouts",
                parentName: "subscription-data",
                list: "/subscriptions/payouts",
                show: "/subscriptions/payouts/:id",
                icon: <CreditCardOutlined />,
                meta: { hide: true },
              },
              {
                name: "workout_preference",
                list: "/workout_preferences",
                create: "/workout_preferences/create",
                edit: "/workout_preferences/edit/:id",
                icon: <ThunderboltOutlined />,
                meta: { hide: true },
              },
              {
                name: "category",
                list: "/categories",
                create: "/categories/create",
                edit: "/categories/edit/:id",
                meta: {
                  label: "Categories",
                  icon: <TagOutlined />,
                  hide: true,
                },
              },
              {
                name: "banner",
                list: "/banners",
                create: "/banners/create",
                edit: "/banners/edit/:id",
                meta: {
                  label: "Banners",
                  icon: <PictureOutlined />,
                },
              },
              {
                name: "card-template",
                list: "/card-templates",
                create: "/card-templates/create",
                edit: "/card-templates/edit/:id",
                meta: {
                  label: "Card Templates",
                  icon: <PictureOutlined />,
                },
              },
              {
                name: "ad-creative",
                list: "/ad-creatives",
                create: "/ad-creatives/create",
                edit: "/ad-creatives/edit/:id",
                meta: {
                  label: "Home Feed Ads",
                  icon: <PictureOutlined />,
                },
              },
              {
                name: "tournament",
                list: "/tournaments",
                show: "/tournaments/:id",
                meta: {
                  label: "Events",
                  icon: <TrophyOutlined />,
                  hide: true,
                },
              },
              {
                name: "scouting-event",
                list: "/scouting-events",
                create: "/scouting-events/create",
                show: "/scouting-events/:id",
                edit: "/scouting-events/edit/:id",
                meta: {
                  label: "Performance Testing",
                  icon: <CompassOutlined />,
                },
              },
              {
                name: "survey-submissions",
                list: "/survey-submissions",
                show: "/survey-submissions/:id",
                meta: {
                  label: "Survey submissions",
                  icon: <FormOutlined />,
                  hide: true,
                },
              },
              {
                name: "session_type",
                list: "/session-types",
                edit: "/session-types/edit/:id",
                meta: {
                  label: "Session Types",
                  icon: <ScheduleOutlined />,
                  hide: true,
                },
              },
              {
                name: "sc_schedule",
                list: "/sc-schedule",
                create: "/sc-schedule/create",
                edit: "/sc-schedule/edit/:id",
                meta: {
                  label: "S&C Schedule",
                  icon: <ScheduleOutlined />,
                },
              },
              // GL-000 DCC: platform-authored fitness classes. Sits directly
              // below S&C because it is the same engine with a different
              // audience — owner-wide instead of performance-group targeted.
              {
                name: "fitness_schedule",
                list: "/fitness-schedule",
                create: "/fitness-schedule/create",
                edit: "/fitness-schedule/edit/:id",
                meta: {
                  label: "Fitness Session",
                  icon: <ScheduleOutlined />,
                },
              },
              {
                name: "clubs",
                list: "/clubs",
                create: "/clubs/create",
                edit: "/clubs/edit/:id",
                show: "/clubs/:id",
                icon: <ShopOutlined />,
                meta: { label: "Clubs" },
              },
              {
                name: "training-questionnaire",
                list: "/training-questionnaire",
                meta: {
                  label: "Training questionnaire",
                  icon: <FormOutlined />,
                },
              },
              {
                name: "settings",
                list: "/settings",
                meta: {
                  label: "Settings",
                  icon: <SettingOutlined />,
                },
              },
            ]}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              useNewQueryKeys: true,
              projectId: "ehctBV-XedgUf-75Fyms",
              title: {
                text: "GymLive",
                icon: (
                  <img
                    src={Icon}
                    alt="GymLive Icon"
                    style={{ width: 24, height: 24 }}
                  />
                ),
              },
              disableTelemetry: true,
            }}
          >
            <Routes>
              <Route
                element={
                  <Authenticated
                    key="authenticated-inner"
                    fallback={<CatchAllNavigate to="/login" />}
                  >
                    <ThemedLayoutV2
                      Header={(props) => (
                        <ThemedHeaderV2 {...props} sticky={true} />
                      )}
                      Sider={(props) => <ThemedSiderV2 {...props} fixed />}
                    >
                      <Outlet />
                    </ThemedLayoutV2>
                  </Authenticated>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="/revenue" element={<RevenuePage />} />
                <Route path="/users">
                  <Route index element={<UsersList />} />
                  <Route path="edit/:id" element={<UserEdit />} />
                  <Route path=":id" element={<UserShow />} />
                </Route>
                <Route path="/workouts">
                  <Route index element={<WorkoutsList />} />
                  <Route path="create" element={<WorkoutCreate />} />
                  <Route path="edit/:id" element={<WorkoutEdit />} />
                  <Route path=":id" element={<WorkoutShow />} />
                </Route>
                <Route path="/missions">
                  <Route index element={<MissionsList />} />
                  <Route path="create" element={<MissionCreate />} />
                  <Route path="edit/:id" element={<MissionEdit />} />
                  <Route path=":id" element={<MissionShow />} />
                </Route>
                <Route path="/reports">
                  <Route path="users" element={<ReportUserList />} />
                  {/* <Route path="users/:id" element={<ReportUserShow />} /> */}
                  <Route path="content" element={<ReportContentList />} />
                  <Route path="content/:id" element={<ReportContentShow />} />
                </Route>
                <Route path="/admins">
                  <Route index element={<AdminList />} />
                  <Route path="create" element={<AdminCreate />} />
                  <Route path="edit/:id" element={<AdminEdit />} />
                </Route>
                <Route path="/lotto-licences">
                  <Route index element={<LottoLicenceList />} />
                  <Route path=":id" element={<LottoLicenceShow />} />
                </Route>
                <Route path="/subscriptions">
                  <Route index element={<SubscribersList />} />
                  <Route path="show/:id" element={<SubscriptionShow />} />
                  <Route path="subscription-plans" element={<PlansList />} />
                  <Route path="plans/edit/:id" element={<PlanEdit />} />
                  <Route path="plans/show/:id" element={<PlanShow />} />
                  <Route path="offers" element={<OfferList />} />
                  <Route path="offers/create" element={<OfferCreate />} />
                  <Route path="offers/edit/:id" element={<OfferEdit />} />
                  <Route path="payouts" element={<PayoutsList />} />
                  <Route path="payouts/:id" element={<PayoutShow />} />
                </Route>
                <Route path="/workout_preferences">
                  <Route index element={<WorkoutPreferencesList />} />
                  <Route path="create" element={<WorkoutPreferenceCreate />} />
                  <Route path="edit/:id" element={<WorkoutPreferenceEdit />} />
                </Route>
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/categories">
                  <Route index element={<CategoriesList />} />
                  <Route path="create" element={<CategoryCreate />} />
                  <Route path="edit/:id" element={<CategoryEdit />} />
                </Route>
                <Route path="/banners">
                  <Route index element={<BannersList />} />
                  <Route path="create" element={<BannerCreate />} />
                  <Route path="edit/:id" element={<BannerEdit />} />
                </Route>
                <Route path="/card-templates">
                  <Route index element={<CardTemplatesList />} />
                  <Route path="create" element={<CardTemplateCreate />} />
                  <Route path="edit/:id" element={<CardTemplateEdit />} />
                </Route>
                <Route path="/ad-creatives">
                  <Route index element={<AdCreativesList />} />
                  <Route path="create" element={<AdCreativeCreate />} />
                  <Route path="edit/:id" element={<AdCreativeEdit />} />
                </Route>
                <Route path="/tournaments">
                  <Route index element={<TournamentsList />} />
                  <Route path=":id" element={<TournamentShow />} />
                </Route>
                <Route path="/scouting-events">
                  <Route index element={<ScoutingEventList />} />
                  <Route path="create" element={<ScoutingEventCreate />} />
                  <Route path="edit/:id" element={<ScoutingEventEdit />} />
                  <Route path=":id" element={<ScoutingEventShow />} />
                </Route>
                <Route path="/survey-submissions">
                  <Route index element={<SurveySubmissionsList />} />
                  <Route path=":id" element={<SurveySubmissionShow />} />
                </Route>
                <Route path="/session-types">
                  <Route index element={<SessionTypesList />} />
                  <Route path="edit/:id" element={<SessionTypeEdit />} />
                </Route>
                <Route path="/sc-schedule">
                  <Route index element={<ScScheduleList />} />
                  <Route path="create" element={<ScScheduleCreate />} />
                  <Route path="edit/:id" element={<ScScheduleEdit />} />
                </Route>
                <Route path="/fitness-schedule">
                  <Route index element={<FitnessScheduleList />} />
                  <Route path="create" element={<FitnessScheduleCreate />} />
                  <Route path="edit/:id" element={<FitnessScheduleEdit />} />
                </Route>
                <Route path="/clubs">
                  <Route index element={<ClubsList />} />
                  <Route path="create" element={<ClubsCreate />} />
                  <Route path="edit/:id" element={<ClubsEdit />} />
                  <Route path=":id" element={<ClubsShow />} />
                </Route>
                <Route
                  path="/training-questionnaire"
                  element={<TrainingQuestionnairePage />}
                />
              </Route>
              <Route path="*" element={<ErrorComponent />} />
              <Route
                element={
                  <Authenticated
                    key="authenticated-outer"
                    fallback={<Outlet />}
                  >
                    <NavigateToResource />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset_password" element={<ResetPassword />} />
                <Route path="/verify" element={<AcceptInvite />} />
              </Route>
            </Routes>

            <UnsavedChangesNotifier />
            <DocumentTitleHandler />
          </Refine>
        </AntdApp>
      </ColorModeContextProvider>
    </BrowserRouter>
  );
}

export default App;
